import "./InputSelect.scss";
import Dropdown from "core/dropdown/Dropdown";
import DropdownLinkItem from "core/dropdown/DropdownLinkItem";
import InputSelectDropdownButton from "assets/icons/InputSelectDropdownButton.svg";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { OnClickOutside } from "utils/functions";
import { TAG_TYPES, VAR_TYPES } from "constants/constants";
import { useEffect, useRef, useState } from "react";
import { ErrorPopup } from "components/alert-popup/ErrorPopup";
import ErrorPopupBtn from "components/alert-popup/ErrorPopupBtn";
import CategoryTag from "core/tags/category-tag/CategoryTag";

const InputSelectDropdown = ({ ...props }) => {
  const displayDropdown = (e) => {
    switch (typeof e?.label) {
      case VAR_TYPES.STRING:
        return e.label;
      case VAR_TYPES.OBJECT:
        return <FormattedMessage {...e.label} />;
      default:
        return <> {e.component}</>;
    }
  };
  return (
    <Dropdown
      id={`input-select-${props.id}-dropwdown`}
      overflow={props.overflow}
      maxHeight={props.maxHeight}
    >
      {props?.options?.map((e, i) => {
        const formattedId =
          e?.label?.id || e?.label?.toLowerCase() || e?.component;
        return (
          <DropdownLinkItem
            id={formattedId}
            key={formattedId}
            onClick={() => props.onClick(e.value)}
          >
            {displayDropdown(e)}
          </DropdownLinkItem>
        );
      })}
    </Dropdown>
  );
};

const InputSelect = ({
  id,
  readOnly,
  label,
  required,
  options,
  value,
  errors,
  isUnderlined,
  hasCategoryTag,
  dataTestId,
  isExport,
  // Shown when no option matches the current value. NOT named `placeholder`:
  // seventeen call sites already pass one, silently swallowed by `...props`
  // until now, and rendering those would print "EUR" in an empty currency cell
  // as if it were the value.
  emptyLabel,
  ...props
}) => {
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const [listening, setListening] = useState(false);
  const dropdownRef = useRef(null);

  const [showErrorPopup, setShowErrorPopup] = useState(false);

  const [val, setVal] = useState(value || "");

  useEffect(() => {
    setVal(value);
  }, [value]);

  useEffect(
    OnClickOutside(listening, setListening, dropdownRef, () => {
      setDropdownOpened(false);
    })
  );

  return (
    <div
      className="input-select"
      onClick={() => !readOnly && setDropdownOpened(!dropdownOpened)}
      id={`input-select-${id}`}
      data-testid={dataTestId}
    >
      <div
        className="overlap-group"
        id={`overlap-group-${id}`}
        ref={dropdownRef}
      >
        <div className="flex-col">
          {label && (
            <div className="label sourcesanspro-normal-shadow-blue-14px">
              <label
                className={
                  errors?.type || errors?.message
                    ? "label-error"
                    : "label-standard"
                }
              >
                {typeof label === "string" ? (
                  label
                ) : (
                  <FormattedMessage
                    id={label.id}
                    defaultMessage={label.defaultMessage}
                  />
                )}
                {required && "*"}
              </label>{" "}
              {(errors?.type || errors?.message) && (
                <ErrorPopupBtn setShowErrorPopup={setShowErrorPopup} />
              )}
              {showErrorPopup && (
                <ErrorPopup
                  errors={errors}
                  showErrorPopup={showErrorPopup}
                  setShowErrorPopup={setShowErrorPopup}
                  label={label}
                  errorMessage={props.errorMessage}
                  errorValue={props.errorValue}
                />
              )}
            </div>
          )}
          {hasCategoryTag && val ? (
            <div style={{ marginBottom: "10px" }}>
              <CategoryTag
                key={1}
                type={TAG_TYPES.POS_COEF}
                label={
                  options?.find((option) => option.value === val)?.label || val
                }
                deleteOption={false}
                onDelete={() => {
                  setVal("");
                  props?.setValue(id, "");
                }}
              />
            </div>
          ) : (
            <div
              className={`input-value colored sourcesanspro-normal-gray-suit-18px`}
              style={
                isUnderlined
                  ? { textDecoration: "underline", fontWeight: "bold" }
                  : {}
              }
              id={`input-value-${id}`}
            >
              {options?.find((elm) => elm.value === val)?.label?.id ? (
                <FormattedMessage
                  {...options.find((elm) => elm.value === val)?.label}
                />
              ) : (
                options?.find((elm) => elm.value === val)?.label ||
                options?.find((elm) => elm.value === val)?.component ||
                (emptyLabel && (
                  <span className="input-value-placeholder">
                    <FormattedMessage {...emptyLabel} />
                  </span>
                ))
              )}
            </div>
          )}
        </div>
        {!readOnly && !isExport && (
          <div className="input-select-trigger">
            <img
              className={`input-select-icon ${dropdownOpened ? "open" : ""}`}
              src={InputSelectDropdownButton}
              id={`input-select-icon-${id}`}
              alt="input-select-icon"
            />
          </div>
        )}
      </div>
      {dropdownOpened && (
        <InputSelectDropdown
          id={id}
          options={options}
          name={props.name}
          onClick={(e) => {
            setVal(e);
            props?.setValue(props.name, e);
          }}
          overflow={"auto"}
          {...props}
        />
      )}
    </div>
  );
};

InputSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.object,
  options: PropTypes.array.isRequired,
  readOnly: PropTypes.bool,
  emptyLabel: PropTypes.object,
  isUnderlined: PropTypes.bool,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  errors: PropTypes.object,
  errorMessage: PropTypes.string,
  errorValue: PropTypes.string,
  hasCategoryTag: PropTypes.bool,
  setValue: PropTypes.func,
  isExport: PropTypes.bool,
};

InputSelect.defaultProps = {
  label: null,
  readOnly: false,
  required: false,
  value: null,
};
export default InputSelect;
